import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import data from "../../data/first.json";
import FirstCard from "./FirstCard";
import SEO from "../common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const FirstExecutions = ({ renderBody }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "First Executions & World Premieres" : "Prime Esecuzioni Assolute & Incisioni";
  const desc = isEnglish
    ? "Works performed in world premiere or modern premiere and first absolute recordings by Nuova Orchestra da Camera Ferruccio Busoni."
    : "Opere eseguite in prima esecuzione assoluta o in tempi moderni e prime registrazioni mondiali della Nuova Orchestra da Camera Ferruccio Busoni.";

  const filteredExecutions = useMemo(() => {
    const list = data[0]?.executions || [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        item.composer.toLowerCase().includes(q) ||
        item.piece.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const filteredRecordings = useMemo(() => {
    const list = data[0]?.recordings || [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        item.composer.toLowerCase().includes(q) ||
        item.piece.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const totalCount =
    (activeTab === "all" || activeTab === "executions" ? filteredExecutions.length : 0) +
    (activeTab === "all" || activeTab === "recordings" ? filteredRecordings.length : 0);

  return (
    <>
      <SEO title={title} description={desc} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* Header intro */}
          <div className="text-center mb-4">
            <p className="text-muted max-w-700 mx-auto mb-0">
              {isEnglish
                ? "An extraordinary heritage of musical rediscoveries, contemporary creations, and world premiere discography recorded by the Orchestra Busoni."
                : "Uno straordinario patrimonio di riscoperte musicali, creazioni contemporanee e incisioni in prima registrazione mondiale realizzate dall'Orchestra Busoni."}
            </p>
          </div>

          {/* Interactive filter & search card */}
          <div className="interactive-filter-card p-4 rounded-4 shadow-sm mb-5 bg-white border">
            <div className="row g-3 align-items-center">
              <div className="col-lg-6">
                <div className="modern-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder={
                      isEnglish
                        ? "Search by composer or work (e.g. Tartini, Allevi, Sofianopulo)..."
                        : "Cerca per compositore o opera (es. Tartini, Allevi, Sofianopulo)..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Cerca brano o compositore"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="modern-search-clear"
                      onClick={() => setSearchQuery("")}
                      aria-label="Cancella ricerca"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="col-lg-6">
                <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                  <button
                    type="button"
                    className={`filter-pill-btn ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    {isEnglish ? "All" : "Tutte"}
                  </button>
                  <button
                    type="button"
                    className={`filter-pill-btn ${activeTab === "executions" ? "active" : ""}`}
                    onClick={() => setActiveTab("executions")}
                  >
                    {isEnglish ? "Concert Premieres" : "Prime Esecuzioni"}
                  </button>
                  <button
                    type="button"
                    className={`filter-pill-btn ${activeTab === "recordings" ? "active" : ""}`}
                    onClick={() => setActiveTab("recordings")}
                  >
                    {isEnglish ? "First Recordings" : "Prime Registrazioni"}
                  </button>
                </div>
              </div>
            </div>

            {/* Results count */}
            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top small text-muted">
              <span>
                {isEnglish
                  ? `Showing ${totalCount} works`
                  : `Visualizzazione di ${totalCount} opere`}
              </span>
              {(searchQuery || activeTab !== "all") && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveTab("all");
                  }}
                >
                  {isEnglish ? "Reset filters" : "Azzera filtri"}
                </button>
              )}
            </div>
          </div>

          {/* Section: Concert Premieres */}
          {(activeTab === "all" || activeTab === "executions") && filteredExecutions.length > 0 && (
            <div className="first-section-block mb-5">
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <h3 className="section-subheading mb-0">
                  {t("first_exe.executions")}
                </h3>
                <span className="badge-count-pill">{filteredExecutions.length}</span>
              </div>
              <FirstCard input={filteredExecutions} renderBody={renderBody} />
            </div>
          )}

          {/* Section: Discographic First Recordings */}
          {(activeTab === "all" || activeTab === "recordings") && filteredRecordings.length > 0 && (
            <div className="first-section-block mb-5">
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <h3 className="section-subheading mb-0">
                  {t("first_exe.recordings")}
                </h3>
                <span className="badge-count-pill">{filteredRecordings.length}</span>
              </div>
              <FirstCard input={filteredRecordings} renderBody={renderBody} />
            </div>
          )}

          {/* Empty fallback */}
          {totalCount === 0 && (
            <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
              <p className="text-muted mb-0 fs-5">
                {isEnglish
                  ? "No works found matching your search."
                  : "Nessuna opera trovata per i criteri specificati."}
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-3 rounded-pill px-3"
                onClick={() => {
                  setSearchQuery("");
                  setActiveTab("all");
                }}
              >
                {isEnglish ? "Reset search" : "Azzera ricerca"}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default FirstExecutions;
